// storybook-check-ignore
import React, { useEffect, useState } from 'react';

import { Modal, ModalProps } from '@opendoor/bricks/complex';
import { CalendlyEmbed } from 'sellDirect/components/legacy/sellerDash/CalendlyEmbed';
import { Box } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';

import { ORVAData } from 'sellDirect/OfferContext';
import { useCalendlyParams } from 'sellDirect/helpers/useCalendlyParams';

/** TODO: Add documentation */
export const CalendlySchedulingModal: React.FC<
  ModalProps & {
    isReschedule?: boolean;
    overridePhoneNumber?: string;
    orvaData: ORVAData;
  }
> = ({ overridePhoneNumber, isReschedule, isOpen, onRequestClose, orvaData, ...props }) => {
  const [dateTimeSelected, setDateTimeSelected] = useState(false);
  const [eventScheduled, setEventScheduled] = useState(false);
  const { refetch, calendar, calendarId } = orvaData;
  const { trackEvent } = useObservability();
  const calendlyParams = useCalendlyParams();
  const params = {
    ...calendlyParams,
    phone: overridePhoneNumber || calendlyParams.phone,
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent('calendly-scheduling-modal-open', '');
    }
  }, [isOpen, trackEvent]);

  useEffect(() => {
    // Calendly API: https://help.calendly.com/hc/en-us/articles/223147027-Embed-options-overview?tab=advanced#6
    const listenToCalendlyEvent = (e: MessageEvent) => {
      // if event is Calendly event
      if (e.data.event && e.data.event.indexOf('calendly') === 0) {
        trackEvent('click', e.data.event, { ...e.data.payload, isReschedule });
        if (e.data.event === 'calendly.date_and_time_selected') {
          setDateTimeSelected(true);
        }
        if (e.data.event === 'calendly.event_scheduled') {
          setEventScheduled(true);
        }
      }
    };
    window.addEventListener('message', listenToCalendlyEvent);
    () => {
      window.removeEventListener('message', listenToCalendlyEvent);
    };
  }, [trackEvent, isReschedule]);

  if (!calendar) {
    return <></>;
  }

  /**
   * Refetch the offer review data in the following conditions:
   * 1. calendly.event_scheduled event tracked.
   * 2. calendly.date_and_time_selected event tracked, and modal is opened for rescheduling. (Calendly doesn't support event listener for event update directly)
   * Then call the modal onRequestClose callback provided by the parent.
   * Track modal close event.
   */
  const handleCloseScheduleModal = () => {
    if (eventScheduled || (isReschedule && dateTimeSelected)) {
      refetch();
    }
    onRequestClose && onRequestClose();
    trackEvent('self-schedule', 'cancel-offer-review-modal');
  };

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      showClose
      onRequestClose={handleCloseScheduleModal}
      hasPadding={false}
      {...props}
    >
      {/* A little padding is required so that the close button doesn't block clicks in the calendly embed */}
      <Box my={[0, 0, 0]} data-testid="calendly-scheduling-embed">
        <CalendlyEmbed calendlyId={calendarId} calendar={calendar} params={params} />
      </Box>
    </Modal>
  );
};
